import type { ArtifactListFiltersUi } from '@/lib/artifact-filters';
import type { ArtifactScope, ArtifactStatus, ArtifactType } from '@/lib/api';

interface ArtifactsFiltersBarProps {
  filters: ArtifactListFiltersUi;
  onChange: (next: Partial<ArtifactListFiltersUi>) => void;
}

const TYPES: ArtifactType[] = ['issue', 'spec', 'adr', 'implementation_plan', 'note'];
const SCOPES: ArtifactScope[] = ['codebase', 'workstream'];
const STATUSES: ArtifactStatus[] = ['draft', 'review', 'approved', 'superseded', 'archived'];

export function ArtifactsFiltersBar({
  filters,
  onChange,
}: ArtifactsFiltersBarProps): React.ReactElement {
  return (
    <div className="flex flex-wrap items-center gap-2 pb-2">
      <select
        value={filters.type ?? ''}
        onChange={e => {
          onChange({
            type: e.target.value === '' ? undefined : (e.target.value as ArtifactType),
            page: 0,
          });
        }}
        className="rounded-md border border-border bg-surface px-2 py-1 text-sm"
        aria-label="Filter by type"
      >
        <option value="">All types</option>
        {TYPES.map(t => (
          <option key={t} value={t}>
            {t}
          </option>
        ))}
      </select>

      <select
        value={filters.scope ?? ''}
        onChange={e => {
          onChange({
            scope: e.target.value === '' ? undefined : (e.target.value as ArtifactScope),
            page: 0,
          });
        }}
        className="rounded-md border border-border bg-surface px-2 py-1 text-sm"
        aria-label="Filter by scope"
      >
        <option value="">All scopes</option>
        {SCOPES.map(s => (
          <option key={s} value={s}>
            {s}
          </option>
        ))}
      </select>

      <select
        value={filters.status ?? ''}
        onChange={e => {
          const next = e.target.value === '' ? undefined : (e.target.value as ArtifactStatus);
          // Selecting status=archived implies "include archived" — otherwise the
          // backend would AND `status = archived` with `status <> archived` and
          // return zero rows. Force the toggle on.
          onChange({
            status: next,
            includeArchived: next === 'archived' ? true : filters.includeArchived,
            page: 0,
          });
        }}
        className="rounded-md border border-border bg-surface px-2 py-1 text-sm"
        aria-label="Filter by status"
      >
        <option value="">All statuses</option>
        {STATUSES.map(s => (
          <option key={s} value={s}>
            {s}
          </option>
        ))}
      </select>

      <input
        type="text"
        value={filters.ownerId ?? ''}
        onChange={e => {
          onChange({
            ownerId: e.target.value.trim() === '' ? undefined : e.target.value.trim(),
            page: 0,
          });
        }}
        placeholder="Owner ID"
        className="w-40 rounded-md border border-border bg-surface px-2 py-1 text-sm"
        aria-label="Filter by owner ID"
      />

      <label className="flex items-center gap-1 text-sm text-text-secondary">
        <input
          type="checkbox"
          checked={filters.includeArchived}
          onChange={e => {
            const include = e.target.checked;
            // Symmetric of the rule above: turning the toggle off must clear an
            // active `status=archived` filter, otherwise the resulting query has
            // no rows that can match.
            onChange({
              includeArchived: include,
              status: !include && filters.status === 'archived' ? undefined : filters.status,
              page: 0,
            });
          }}
        />
        Include archived
      </label>
    </div>
  );
}
